import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Sticker, Plus, Trash2, Loader2 } from 'lucide-react';
import { Button } from 'src/@/components/ui/button';

// ----------------------------------------------------------------------

StickerPicker.propTypes = {
  disabled: PropTypes.bool,
  figurinhas: PropTypes.array,
  loading: PropTypes.bool,
  creating: PropTypes.bool,
  onOpen: PropTypes.func,
  onCreate: PropTypes.func,
  onSend: PropTypes.func,
  onDelete: PropTypes.func,
  alignRight: PropTypes.bool,
};

export default function StickerPicker({
  disabled,
  figurinhas = [],
  loading,
  creating,
  onOpen,
  onCreate,
  onSend,
  onDelete,
  alignRight = false,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [deletingId, setDeletingId] = useState(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const fileInputRef = useRef(null);

  const panelWidth = 320;
  const panelHeight = 380;

  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();

      let top = rect.top - panelHeight - 8;
      let left = alignRight ? rect.right - panelWidth : rect.left;

      if (top < 8) {
        top = rect.bottom + 8;
      }
      if (left < 8) {
        left = 8;
      }
      if (left + panelWidth > window.innerWidth - 8) {
        left = window.innerWidth - panelWidth - 8;
      }

      setPosition({ top, left });
    }
  }, [alignRight]);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (e) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  useEffect(() => {
    if (open) {
      updatePosition();
      onOpenRef.current?.();
    }
  }, [open, updatePosition]);

  const triggerPicker = (e) => {
    e.preventDefault();
    setOpen((prev) => !prev);
  };

  const handleSendClick = async (figurinha) => {
    setOpen(false);
    await onSend?.(figurinha);
  };

  const handleDeleteClick = async (e, figurinha) => {
    e.stopPropagation();
    try {
      setDeletingId(figurinha._id);
      await onDelete?.(figurinha);
    } finally {
      setDeletingId(null);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    await onCreate?.(file);
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />
      <Button
        ref={buttonRef}
        variant="ghost"
        size="icon"
        disabled={disabled}
        onClick={triggerPicker}
        className="h-8 w-8"
      >
        <Sticker className={`h-4 w-4 ${disabled ? 'text-gray-400' : 'text-slate-800'}`} />
      </Button>
      {open && createPortal(
        <div
          ref={panelRef}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            width: panelWidth,
            height: panelHeight,
            zIndex: 99999,
            borderRadius: 12,
            overflow: 'hidden',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
            pointerEvents: 'auto',
          }}
          className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 flex flex-col"
        >
          <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {t('chat.stickers.title')}
            </span>
            <Button
              variant="ghost"
              size="icon"
              disabled={creating}
              onClick={() => fileInputRef.current?.click()}
              className="h-7 w-7"
              title={t('chat.stickers.create')}
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin text-[#7C3AED]" /> : <Plus className="h-4 w-4 text-[#7C3AED]" />}
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto p-2">
            {loading ? (
              <div className="w-full h-full flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            ) : figurinhas.length === 0 ? (
              <div className="w-full h-full flex flex-col items-center justify-center text-center px-4 gap-1">
                <Sticker className="h-6 w-6 text-gray-300 dark:text-gray-600" />
                <p className="text-xs text-gray-400 dark:text-gray-500">{t('chat.stickers.empty')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {figurinhas.map((figurinha) => (
                  <div
                    key={figurinha._id}
                    className="relative group w-[64px] h-[64px] rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center cursor-pointer"
                    onClick={() => handleSendClick(figurinha)}
                  >
                    <img
                      src={figurinha.url}
                      alt="figurinha"
                      className="max-w-[56px] max-h-[56px] object-contain pointer-events-none"
                    />
                    <button
                      type="button"
                      onClick={(e) => handleDeleteClick(e, figurinha)}
                      disabled={deletingId === figurinha._id}
                      title={t('chat.stickers.delete')}
                      className="absolute -top-1 -right-1 hidden group-hover:flex h-5 w-5 items-center justify-center rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-sm hover:bg-red-50 dark:hover:bg-red-500/10"
                    >
                      {deletingId === figurinha._id ? (
                        <Loader2 className="h-3 w-3 animate-spin text-gray-400" />
                      ) : (
                        <Trash2 className="h-3 w-3 text-red-500" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
